/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Form,
  Row,
  Col,
  Input,
  Button,
  Spin,
  Divider,
  Typography,
} from 'antd';
import Select from 'components/Select';
import Table from 'components/Table';
import CurrencyInput from 'components/CurrencyInput';
import FormSubmitControls from 'components/FormSubmitControls';
import { onError } from 'utils/handlers';
import currencyFormatter from 'utils/currencyFormatter';

// 1. API para llenar el combo (Catálogo de periodos)
import { getPeriodosDeNominasDetalles } from 'api/nominas/periodos/periodosDeNominasDetalle';

// 2. API Nueva (CRUD del Nieto para guardar/consultar)
import {
  getSolicitudesPercepcionesEmpleadosPeriodos,
  postSolicitudPercepcionEmpleadoPeriodo,
  deleteSolicitudPercepcionEmpleadoPeriodo,
} from 'api/nominas/solicitudes/solicitudesPercepcionesEmpleadosPeriodos';

const ModalAgregarPeriodosDevolucion = ({
  open,
  onCancel,
  empleadoSelected,
  periodosDisponibles,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dataPeriodos, setDataPeriodos] = useState([]); // Lista para la tabla
  const [periodosCombo, setPeriodosCombo] = useState([]); // Lista para el Select
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // Función para recargar la tabla de periodos guardados del empleado
  const fetchPeriodosGuardados = async () => {
    if (empleadoSelected?.id) {
      const guardados = await getSolicitudesPercepcionesEmpleadosPeriodos({
        solicitud_percepcion_empleado: empleadoSelected.id,
      });
      // El API devuelve un array, lo seteamos al estado
      setDataPeriodos(guardados || []);
    }
  };

  useEffect(() => {
    let mounted = true;
    const init = async () => {
      if (open) {
        setLoading(true);
        try {
          if (periodosDisponibles && periodosDisponibles.length > 0) {
            setPeriodosCombo(periodosDisponibles);
          } else {
            const _periodos = await getPeriodosDeNominasDetalles();
            if (mounted) setPeriodosCombo(_periodos);
          }

          if (mounted) await fetchPeriodosGuardados();
        } catch (error) {
          onError(error);
        } finally {
          if (mounted) setLoading(false);
        }
      }
    };
    init();
    return () => { mounted = false; };
  }, [open, empleadoSelected]);

  const onFinish = async (values) => {
    try {
      setLoading(true);

      const payload = {
        solicitud_percepcion_empleado: empleadoSelected.id, // ID del padre
        periodo_de_nomina_detalle: values.periodo_nomina, // ID del periodo seleccionado
        importe: values.importe,

      };

      const respuesta = await postSolicitudPercepcionEmpleadoPeriodo(payload);

      if (respuesta) {
        // Limpiar formulario
        form.resetFields(['periodo_nomina', 'importe']);

        // Recargar la tabla para mostrar el nuevo registro con su ID real y descripción
        await fetchPeriodosGuardados();
      }
    } catch (err) {
      onError(err, setLoading);
    } finally {
      setLoading(false);
    }
  };

  // Función opcional para borrar un periodo desde la tabla del modal
  const onDeletePeriodo = async () => {
    try {
      if (selectedRowKeys.length === 0) return; // Validación extra

      setLoading(true);
      const [idToDelete] = selectedRowKeys; // Obtenemos el ID seleccionado

      const success = await deleteSolicitudPercepcionEmpleadoPeriodo(idToDelete);

      if (success) {
        await fetchPeriodosGuardados();
        setSelectedRowKeys([]); // Limpiamos la selección después de borrar
      }
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      titleText: 'Periodo de Nómina',
      dataIndex: 'periodo_descripcion', // Este campo viene calculado del Serializer
      key: 'periodo_descripcion',
      width: 250,
    },
    {
      titleText: 'Importe',
      dataIndex: 'importe',
      key: 'importe',
      width: 150,
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estatus_descripcion', // Campo calculado del Serializer
      key: 'estatus',
      width: 150,
    },
  ];

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio', // Selección única
    onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys),
  };

  return (
    <Modal
      title={(
        <FormSubmitControls
          label={`Periodos de Descuentos Indebidos - ${empleadoSelected?.nombre_empleado || 'Empleado'}`}
          onFinish={() => form.submit()} // Esto dispara el onFinish del <Form> de abajo
          onCancel={onCancel}
          onFinishText="Guardar"
        />
      )}
      open={open}
      onCancel={onCancel}
      width="80%"
      footer={null} // Quitamos el footer manual
      centered
      destroyOnClose
      forceRender
    >
      <Spin spinning={loading}>
        {/* TABLA SUPERIOR: Periodos ya guardados */}
        <div style={{ marginBottom: 20 }}>
          <Table
            cols={columns}
            data={dataPeriodos}
            allowPagination={false}
            allowExportParam={false}
            scroll={{ y: 200 }}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            // Agregamos botón de borrar en la tabla
            controls={{
              onClickDelete: onDeletePeriodo,
            }}
            permission={{
              hasAllViewPermission: true,
              add: true,
              change: true,
              delete: true,
            }}
            hasAllViewPermission
          />
        </div>

        <Divider orientation="left">AGREGAR PERIODOS DE DESCUENTOS INDEBIDOS</Divider>

        {/* FORMULARIO INFERIOR */}
        <Form
          form={form}
          layout="horizontal"
          onFinish={onFinish}
          initialValues={{
            estatus_visual: 'Activo', // Solo visual
          }}
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
        >
          <Form.Item
            name="periodo_nomina"
            label="Periodo de Nómina"
            rules={[{ required: true, message: 'Seleccione un periodo' }]}
          >
            <Select
              dataSource={periodosCombo}
              keyProp="id"
              labelProp="numero_de_nomina"
              // Render personalizado para que se vea como en la imagen: "01 (2025...)"
              render={(item) => `${item.numero_de_nomina} (${item.fecha_inicial} - ${item.fecha_final})`}

            />
          </Form.Item>

          <Row>
            <Col span={24}>
              <CurrencyInput
                form={form}
                name="importe"
                label="Importe"
                rules={[{ required: true, message: 'Ingrese el importe' }]}
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 18 }}

              />
            </Col>
          </Row>

          <Form.Item
            name="estatus_visual"
            label="Estatus"
          >
            <Input disabled style={{ backgroundColor: '#f5f5f5', color: 'rgba(0, 0, 0, 0.85)' }} />
          </Form.Item>

        </Form>
      </Spin>
    </Modal>
  );
};

ModalAgregarPeriodosDevolucion.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  empleadoSelected: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nombre_empleado: PropTypes.string,
  }),
  // eslint-disable-next-line react/forbid-prop-types
  periodosDisponibles: PropTypes.arrayOf(PropTypes.object),
};

ModalAgregarPeriodosDevolucion.defaultProps = {
  empleadoSelected: {},
  periodosDisponibles: [],
};

export default ModalAgregarPeriodosDevolucion;
